import api from '@/lib/api';
import type {
  ApiResponse,
  PaginationMeta,
  HseIncident,
  HseInjuredPerson,
  HseWitness,
  HseInvestigationFinding,
  HseEvidence,
  HseNearMiss,
  HseWorkflowEvent,
  HseCapa,
  HseRegulatoryNotification,
  HseExposureHours,
  HseEvidenceCustodyEvent,
  HseInvestigationApproval,
  HseApprovalDelegation,
  HseCauseCode,
  HseBarrierCode,
  HseQualityGate,
  HseQualityGateEvaluation,
  HseFiveWhysChain,
  HseFiveWhysStep,
  HseFishboneDiagram,
  HseFishboneBranch,
  HseTimelineEvent,
  HseBarrierAnalysis,
  HseLessonLearned,
  HseSafetyBulletin,
  HseTrainingRecommendation,
  CreateIncidentRequest,
  UpdateIncidentRequest,
  IncidentListQuery,
  StartInvestigationRequest,
  TransitionReasonRequest,
  CreateInjuredPersonRequest,
  UpdateInjuredPersonRequest,
  CreateWitnessRequest,
  UpdateWitnessRequest,
  CreateFindingRequest,
  UpdateFindingRequest,
  CreateEvidenceRequest,
  CreateNearMissRequest,
  UpdateNearMissRequest,
  NearMissListQuery,
  ReviewNearMissRequest,
  ConvertNearMissRequest,
  CreateCapaRequest,
  UpdateCapaRequest,
  CapaListQuery,
  CapaOperationalSummary,
  AssignCapaRequest,
  VerifyCapaRequest,
  SubmitEffectivenessReviewRequest,
  CancelCapaRequest,
  HseCapaActivityEntry,
  HseCapaEvidence,
  HseCapaComment,
  AttachCapaEvidenceRequest,
  AddCapaCommentRequest,
  EditCapaCommentRequest,
  BulletinSourceDisplay,
  HseManagementReview,
  HseManagementReviewItem,
  ManagementReviewSourceDisplay,
  HseLessonLearnedRegisterRow,
  HseIncidentLifecycleReadiness,
  HseIncidentOperationalMemory,
} from '@amuster/types';
import type {
  CreateManagementReviewItemPayload,
  UpdateManagementReviewItemPayload,
} from '@/lib/serializeManagementReviewItemPayload';

// ── PR INV-TEAM-A1 — local DTOs (no packages/types change in this lane) ─
//
// These types describe the shape of GET /api/hse/incidents/:id/
// investigation-team. They mirror InvestigationTeamMember /
// InvestigationTeamListResponse on the backend (apps/api/src/modules/
// incident/investigation-team.service.ts). When this lane graduates and
// the table moves to a write workspace, these types should be promoted
// to `@amuster/types` in the same PR as the write endpoints.

export type HseInvestigationTeamRoleCode =
  | 'lead'
  | 'hse_officer'
  | 'ops'
  | 'technical'
  | 'hr'
  | 'worker_rep'
  | 'external_sme'
  | 'other';

export type HseInvestigationTeamMemberType = 'internal' | 'external';

export interface HseInvestigationTeamMember {
  id: string;
  incident_id: string;
  role_code: HseInvestigationTeamRoleCode;
  member_type: HseInvestigationTeamMemberType;
  user_id: string | null;
  display_name: string | null;
  email: string | null;
  external_member_name: string | null;
  competence_tags: string[];
  invited_at: string | null;
  accepted_at: string | null;
}

export interface HseInvestigationTeamList {
  incident_id: string;
  members: HseInvestigationTeamMember[];
  member_count: number;
  removed_count: number;
}

// ── PR INV-STATEMENT-CONSENT-A2-2 — local DTOs (no packages/types change) ─
//
// Compact, data-minimized read-only DTOs for GET /api/hse/incidents/:id/
// statement-consent. Mirrors StatementConsentService on the backend
// (apps/api/src/modules/incident/statement-consent.service.ts).
//
// Sensitive free-text columns (contact_summary, external_reference,
// guardian_name, notes_summary, statement_text) are intentionally
// absent from these DTOs — the server does not project them under the
// hse:incident:statement_consent:view permission, and the client must
// not assume they are present. They remain in the schema (mig 381)
// for future PII-specific permissions.

export type HseParticipantRoleCode =
  | 'witness'
  | 'injured_worker'
  | 'reporter'
  | 'guardian_or_parent'
  | 'supervisor'
  | 'investigator'
  | 'external_party'
  | 'other';

export type HseParticipantType =
  | 'internal_user'
  | 'external_person'
  | 'student'
  | 'contractor'
  | 'visitor'
  | 'anonymous'
  | 'other';

export type HseConsentTypeCode =
  | 'interview'
  | 'audio_recording'
  | 'video_recording'
  | 'written_statement'
  | 'medical_or_clinic_context'
  | 'minor_guardian'
  | 'data_processing'
  | 'ai_processing'
  | 'other';

export type HseConsentStatus =
  | 'not_required'
  | 'required'
  | 'requested'
  | 'granted'
  | 'refused'
  | 'withdrawn'
  | 'expired'
  | 'deferred';

export type HseStatementTypeCode =
  | 'witness_statement'
  | 'injured_worker_statement'
  | 'initial_account'
  | 'follow_up_statement'
  | 'correction_or_addendum'
  | 'refusal_to_statement'
  | 'other';

export type HseStatementStatus =
  | 'draft'
  | 'recorded'
  | 'reviewed'
  | 'signed'
  | 'withdrawn'
  | 'refused'
  | 'superseded';

export interface HseIncidentParticipant {
  id: string;
  incident_id: string;
  participant_role_code: HseParticipantRoleCode;
  participant_type: HseParticipantType;
  user_id: string | null;
  display_name: string | null;
  is_minor: boolean;
  relationship_to_incident: string | null;
  created_at: string;
  updated_at: string;
}

export interface HseIncidentConsentRecord {
  id: string;
  incident_id: string;
  participant_id: string | null;
  consent_type_code: HseConsentTypeCode;
  consent_status: HseConsentStatus;
  consent_required: boolean;
  consent_requested_at: string | null;
  consent_granted_at: string | null;
  consent_refused_at: string | null;
  consent_expires_at: string | null;
  consent_form_name: string | null;
  consent_form_version: string | null;
  signed_artifact_evidence_id: string | null;
  consent_scope_codes: string[];
  ai_processing_allowed: boolean;
  created_at: string;
  updated_at: string;
}

export interface HseIncidentStatementRecord {
  id: string;
  incident_id: string;
  participant_id: string | null;
  statement_type_code: HseStatementTypeCode;
  statement_status: HseStatementStatus;
  statement_taken_at: string | null;
  statement_taken_by_user_id: string | null;
  statement_summary: string | null;
  language_code: string | null;
  translated_from_statement_id: string | null;
  consent_record_id: string | null;
  evidence_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface HseStatementConsentOverview {
  incident_id: string;
  participants: HseIncidentParticipant[];
  consent_records: HseIncidentConsentRecord[];
  statement_records: HseIncidentStatementRecord[];
  participant_count: number;
  consent_record_count: number;
  statement_record_count: number;
  removed_participant_count: number;
  removed_consent_record_count: number;
  removed_statement_record_count: number;
}

export const hseService = {
  // ── Incidents — CRUD ────────────────────────────────────────────────────

  createIncident(data: CreateIncidentRequest) {
    return api.post<ApiResponse<HseIncident>>('/hse/incidents', data);
  },

  listIncidents(query?: IncidentListQuery) {
    return api.get<ApiResponse<HseIncident[]> & { meta: PaginationMeta }>('/hse/incidents', { params: query });
  },

  getIncident(id: string) {
    return api.get<ApiResponse<HseIncident>>(`/hse/incidents/${id}`);
  },

  updateIncident(id: string, data: UpdateIncidentRequest) {
    return api.patch<ApiResponse<HseIncident>>(`/hse/incidents/${id}`, data);
  },

  // ── Incidents — Lifecycle ───────────────────────────────────────────────

  acknowledgeIncident(id: string, data?: TransitionReasonRequest) {
    return api.post<ApiResponse<HseIncident>>(`/hse/incidents/${id}/acknowledge`, data ?? {});
  },

  startInvestigation(id: string, data: StartInvestigationRequest) {
    return api.post<ApiResponse<HseIncident>>(`/hse/incidents/${id}/investigate`, data);
  },

  submitForReview(id: string, data?: TransitionReasonRequest) {
    return api.post<ApiResponse<HseIncident>>(`/hse/incidents/${id}/review`, data ?? {});
  },

  closeIncident(id: string, data?: TransitionReasonRequest) {
    return api.post<ApiResponse<HseIncident>>(`/hse/incidents/${id}/close`, data ?? {});
  },

  reopenIncident(id: string, data?: TransitionReasonRequest) {
    return api.post<ApiResponse<HseIncident>>(`/hse/incidents/${id}/reopen`, data ?? {});
  },

  escalateIncident(id: string, data?: TransitionReasonRequest) {
    return api.post<ApiResponse<HseIncident>>(`/hse/incidents/${id}/escalate`, data ?? {});
  },

  // ── Injured Persons ─────────────────────────────────────────────────────

  addInjuredPerson(incidentId: string, data: CreateInjuredPersonRequest) {
    return api.post<ApiResponse<HseInjuredPerson>>(`/hse/incidents/${incidentId}/injured-persons`, data);
  },

  listInjuredPersons(incidentId: string) {
    return api.get<ApiResponse<HseInjuredPerson[]>>(`/hse/incidents/${incidentId}/injured-persons`);
  },

  updateInjuredPerson(incidentId: string, personId: string, data: UpdateInjuredPersonRequest) {
    return api.patch<ApiResponse<HseInjuredPerson>>(`/hse/incidents/${incidentId}/injured-persons/${personId}`, data);
  },

  deleteInjuredPerson(incidentId: string, personId: string) {
    return api.delete(`/hse/incidents/${incidentId}/injured-persons/${personId}`);
  },

  // ── Witnesses ───────────────────────────────────────────────────────────

  addWitness(incidentId: string, data: CreateWitnessRequest) {
    return api.post<ApiResponse<HseWitness>>(`/hse/incidents/${incidentId}/witnesses`, data);
  },

  listWitnesses(incidentId: string) {
    return api.get<ApiResponse<HseWitness[]>>(`/hse/incidents/${incidentId}/witnesses`);
  },

  updateWitness(incidentId: string, witnessId: string, data: UpdateWitnessRequest) {
    return api.patch<ApiResponse<HseWitness>>(`/hse/incidents/${incidentId}/witnesses/${witnessId}`, data);
  },

  // ── Investigation Findings ──────────────────────────────────────────────

  addFinding(incidentId: string, data: CreateFindingRequest) {
    return api.post<ApiResponse<HseInvestigationFinding>>(`/hse/incidents/${incidentId}/findings`, data);
  },

  listFindings(incidentId: string) {
    return api.get<ApiResponse<HseInvestigationFinding[]>>(`/hse/incidents/${incidentId}/findings`);
  },

  updateFinding(incidentId: string, findingId: string, data: UpdateFindingRequest) {
    return api.patch<ApiResponse<HseInvestigationFinding>>(`/hse/incidents/${incidentId}/findings/${findingId}`, data);
  },

  // ── Evidence ────────────────────────────────────────────────────────────

  addEvidence(incidentId: string, data: CreateEvidenceRequest) {
    return api.post<ApiResponse<HseEvidence>>(`/hse/incidents/${incidentId}/evidence`, data);
  },

  listEvidence(incidentId: string) {
    return api.get<ApiResponse<HseEvidence[]>>(`/hse/incidents/${incidentId}/evidence`);
  },

  // PR4.5: real file upload — multipart/form-data. Computes file_name /
  // mime_type / file_size_bytes / content_hash server-side from the bytes.
  uploadIncidentEvidence(
    incidentId: string,
    args: { file: File; evidenceType: string; description?: string | null },
  ) {
    const fd = new FormData();
    fd.append('file', args.file);
    fd.append('evidence_type', args.evidenceType);
    if (args.description) fd.append('description', args.description);
    return api.post<ApiResponse<HseEvidence>>(
      `/hse/incidents/${incidentId}/evidence/upload`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },

  // PR4.5: authenticated download URL (raw API path). Caller composes
  // it into <img src> / <iframe src> via fetch+blob to keep the auth
  // cookie attached.
  getIncidentEvidenceDownloadPath(incidentId: string, evidenceId: string): string {
    return `/api/hse/incidents/${incidentId}/evidence/${evidenceId}/download`;
  },

  // ── Workflow Events (Timeline) ──────────────────────────────────────────

  listWorkflowEvents(incidentId: string) {
    return api.get<ApiResponse<HseWorkflowEvent[]>>(`/hse/incidents/${incidentId}/workflow-events`);
  },

  // ── Near Misses ─────────────────────────────────────────────────────────

  createNearMiss(data: CreateNearMissRequest) {
    return api.post<ApiResponse<HseNearMiss>>('/hse/incidents/near-misses', data);
  },

  listNearMisses(query?: NearMissListQuery) {
    return api.get<ApiResponse<HseNearMiss[]> & { meta: PaginationMeta }>('/hse/incidents/near-misses', { params: query });
  },

  getNearMiss(id: string) {
    return api.get<ApiResponse<HseNearMiss>>(`/hse/incidents/near-misses/${id}`);
  },

  updateNearMiss(id: string, data: UpdateNearMissRequest) {
    return api.patch<ApiResponse<HseNearMiss>>(`/hse/incidents/near-misses/${id}`, data);
  },

  reviewNearMiss(id: string, data: ReviewNearMissRequest) {
    return api.post<ApiResponse<HseNearMiss>>(`/hse/incidents/near-misses/${id}/review`, data);
  },

  convertNearMiss(id: string, data?: ConvertNearMissRequest) {
    return api.post<ApiResponse<{ near_miss: HseNearMiss; incident: HseIncident }>>(`/hse/incidents/near-misses/${id}/convert`, data ?? {});
  },

  // ── Corrective Actions (CAPA) ─────────────────────────────────────────

  listCapas(query?: CapaListQuery) {
    return api.get<ApiResponse<HseCapa[]> & { meta: PaginationMeta }>('/hse/incidents/corrective-actions', { params: query });
  },

  getCapaSummary() {
    return api.get<ApiResponse<CapaOperationalSummary>>('/hse/incidents/corrective-actions/summary');
  },

  listIncidentCapas(incidentId: string, query?: CapaListQuery) {
    return api.get<ApiResponse<HseCapa[]> & { meta: PaginationMeta }>(`/hse/incidents/${incidentId}/corrective-actions`, { params: query });
  },

  getCapa(capaId: string) {
    return api.get<ApiResponse<HseCapa>>(`/hse/incidents/corrective-actions/${capaId}`);
  },

  createCapa(incidentId: string, data: CreateCapaRequest) {
    return api.post<ApiResponse<HseCapa>>(`/hse/incidents/${incidentId}/corrective-actions`, data);
  },

  updateCapa(capaId: string, data: UpdateCapaRequest) {
    return api.patch<ApiResponse<HseCapa>>(`/hse/incidents/corrective-actions/${capaId}`, data);
  },

  assignCapa(capaId: string, data: AssignCapaRequest) {
    return api.post<ApiResponse<HseCapa>>(`/hse/incidents/corrective-actions/${capaId}/assign`, data);
  },

  startCapaProgress(capaId: string) {
    return api.post<ApiResponse<HseCapa>>(`/hse/incidents/corrective-actions/${capaId}/start`, {});
  },

  completeCapa(capaId: string) {
    return api.post<ApiResponse<HseCapa>>(`/hse/incidents/corrective-actions/${capaId}/complete`, {});
  },

  verifyCapa(capaId: string, data: VerifyCapaRequest) {
    return api.post<ApiResponse<HseCapa>>(`/hse/incidents/corrective-actions/${capaId}/verify`, data);
  },

  submitEffectivenessReview(capaId: string, data: SubmitEffectivenessReviewRequest) {
    return api.post<ApiResponse<HseCapa>>(
      `/hse/incidents/corrective-actions/${capaId}/effectiveness-review`,
      data,
    );
  },

  cancelCapa(capaId: string, data?: CancelCapaRequest) {
    return api.post<ApiResponse<HseCapa>>(`/hse/incidents/corrective-actions/${capaId}/cancel`, data ?? {});
  },

  searchCapaAssignableUsers(
    q: string,
    limit?: number,
    // PR11-B: optional campus context. Pass `incident_id` from the Add CAPA
    // dialog (incident detail page knows it) or `capa_id` from any
    // reassignment surface (incident detail Assign dialog + standalone
    // /hse/capas/:id Assign dialog). Server resolves campus from whichever
    // id is supplied — frontend cannot pass a raw campus_id. Both at once
    // is rejected by the Zod schema with 400. Bulk-assign omits both
    // (search stays tenant-wide; per-row validation enforces correctness).
    opts?: { incident_id?: string; capa_id?: string },
  ) {
    return api.get<
      ApiResponse<
        Array<{
          id: string;
          name_en: string;
          name_ar: string | null;
          photo_url: string | null;
          department: string | null;
          role: string;
        }>
      >
    >('/hse/incidents/corrective-actions/assignable-users', {
      params: {
        q,
        limit,
        incident_id: opts?.incident_id,
        capa_id: opts?.capa_id,
      },
    });
  },

  listCapaActivity(capaId: string) {
    return api.get<ApiResponse<HseCapaActivityEntry[]>>(
      `/hse/incidents/corrective-actions/${capaId}/activity`,
    );
  },

  // ── CAPA Evidence attachments (PR2) ───────────────────────────────────

  listCapaEvidence(capaId: string) {
    return api.get<ApiResponse<HseCapaEvidence[]>>(
      `/hse/incidents/corrective-actions/${capaId}/evidence`,
    );
  },

  attachCapaEvidence(capaId: string, data: AttachCapaEvidenceRequest) {
    return api.post<ApiResponse<HseCapaEvidence>>(
      `/hse/incidents/corrective-actions/${capaId}/evidence`,
      data,
    );
  },

  removeCapaEvidence(capaId: string, capaEvidenceId: string) {
    return api.delete(
      `/hse/incidents/corrective-actions/${capaId}/evidence/${capaEvidenceId}`,
    );
  },

  // ── CAPA Comments (PR2) ───────────────────────────────────────────────

  listCapaComments(capaId: string) {
    return api.get<ApiResponse<HseCapaComment[]>>(
      `/hse/incidents/corrective-actions/${capaId}/comments`,
    );
  },

  addCapaComment(capaId: string, data: AddCapaCommentRequest) {
    return api.post<ApiResponse<HseCapaComment>>(
      `/hse/incidents/corrective-actions/${capaId}/comments`,
      data,
    );
  },

  editCapaComment(capaId: string, commentId: string, data: EditCapaCommentRequest) {
    return api.patch<ApiResponse<HseCapaComment>>(
      `/hse/incidents/corrective-actions/${capaId}/comments/${commentId}`,
      data,
    );
  },

  deleteCapaComment(capaId: string, commentId: string) {
    return api.delete(
      `/hse/incidents/corrective-actions/${capaId}/comments/${commentId}`,
    );
  },

  // ── Regulatory Notifications ──────────────────────────────────────────────

  listRegulatoryRules(query?: { page?: number; limit?: number; agency?: string; is_active?: boolean }) {
    return api.get<ApiResponse<HseRegulatoryNotification[]> & { meta: PaginationMeta }>('/hse/incidents/regulatory/rules', { params: query });
  },

  createRegulatoryNotification(incidentId: string, data: Record<string, unknown>) {
    return api.post<ApiResponse<HseRegulatoryNotification>>(`/hse/incidents/${incidentId}/regulatory`, data);
  },

  listRegulatoryNotifications(incidentId: string, query?: { page?: number; limit?: number; status?: string }) {
    return api.get<ApiResponse<HseRegulatoryNotification[]> & { meta: PaginationMeta }>(`/hse/incidents/${incidentId}/regulatory`, { params: query });
  },

  getRegulatoryNotification(incidentId: string, notificationId: string) {
    return api.get<ApiResponse<HseRegulatoryNotification>>(`/hse/incidents/${incidentId}/regulatory/${notificationId}`);
  },

  updateRegulatoryNotification(incidentId: string, notificationId: string, data: Record<string, unknown>) {
    return api.patch<ApiResponse<HseRegulatoryNotification>>(`/hse/incidents/${incidentId}/regulatory/${notificationId}`, data);
  },

  // ── Exposure Hours ────────────────────────────────────────────────────────

  createExposureHours(data: Record<string, unknown>) {
    return api.post<ApiResponse<HseExposureHours>>('/hse/incidents/exposure-hours', data);
  },

  listExposureHours(query?: { page?: number; limit?: number; campus_id?: string; period_type?: string }) {
    return api.get<ApiResponse<HseExposureHours[]> & { meta: PaginationMeta }>('/hse/incidents/exposure-hours', { params: query });
  },

  getExposureHours(id: string) {
    return api.get<ApiResponse<HseExposureHours>>(`/hse/incidents/exposure-hours/${id}`);
  },

  updateExposureHours(id: string, data: Record<string, unknown>) {
    return api.patch<ApiResponse<HseExposureHours>>(`/hse/incidents/exposure-hours/${id}`, data);
  },

  deleteExposureHours(id: string) {
    return api.delete(`/hse/incidents/exposure-hours/${id}`);
  },

  // ── Evidence Governance ───────────────────────────────────────────────────

  updateEvidenceGovernance(incidentId: string, evidenceId: string, data: Record<string, unknown>) {
    return api.patch<ApiResponse<HseEvidence>>(`/hse/incidents/${incidentId}/evidence/${evidenceId}/governance`, data);
  },

  createCustodyEvent(incidentId: string, evidenceId: string, data: Record<string, unknown>) {
    return api.post<ApiResponse<HseEvidenceCustodyEvent>>(`/hse/incidents/${incidentId}/evidence/${evidenceId}/custody`, data);
  },

  listCustodyEvents(incidentId: string, evidenceId: string, query?: { page?: number; limit?: number }) {
    return api.get<ApiResponse<HseEvidenceCustodyEvent[]> & { meta: PaginationMeta }>(`/hse/incidents/${incidentId}/evidence/${evidenceId}/custody`, { params: query });
  },

  // ── Work-Relatedness Determinations (PR D-UI) ────────────────────────────
  //
  // Append-only governed write for incident G1. Server hard-codes
  // source='manual' for this lane and binds tenant_id / incident_id /
  // determined_by_user_id from the authenticated session — none of
  // those fields are accepted in the body. The response shape is not
  // consumed by the UI: the lifecycle-readiness panel refreshes via
  // a cache invalidation on the existing query key, and re-fetches
  // the new G1 status from the read endpoint.
  createWorkRelatednessDetermination(
    incidentId: string,
    data: {
      determination:
        | 'work_related'
        | 'not_work_related'
        | 'undetermined'
        | 'not_applicable';
      rationale?: string | null;
      determination_context_code?: string | null;
    },
  ) {
    return api.post<ApiResponse<unknown>>(
      `/hse/incidents/${incidentId}/work-relatedness-determinations`,
      data,
    );
  },

  // ── Investigation Approvals ───────────────────────────────────────────────

  createApproval(incidentId: string, data: Record<string, unknown>) {
    return api.post<ApiResponse<HseInvestigationApproval>>(`/hse/incidents/${incidentId}/approvals`, data);
  },

  listApprovals(incidentId: string, query?: { page?: number; limit?: number; meaning?: string; decision?: string }) {
    return api.get<ApiResponse<HseInvestigationApproval[]> & { meta: PaginationMeta }>(`/hse/incidents/${incidentId}/approvals`, { params: query });
  },

  getApproval(incidentId: string, approvalId: string) {
    return api.get<ApiResponse<HseInvestigationApproval>>(`/hse/incidents/${incidentId}/approvals/${approvalId}`);
  },

  // ── Approval Delegations ──────────────────────────────────────────────────

  createDelegation(data: Record<string, unknown>) {
    return api.post<ApiResponse<HseApprovalDelegation>>('/hse/incidents/delegations', data);
  },

  listDelegations(query?: { page?: number; limit?: number; scope?: string; is_active?: boolean }) {
    return api.get<ApiResponse<HseApprovalDelegation[]> & { meta: PaginationMeta }>('/hse/incidents/delegations', { params: query });
  },

  revokeDelegation(delegationId: string, data?: { reason?: string }) {
    return api.post<ApiResponse<HseApprovalDelegation>>(`/hse/incidents/delegations/${delegationId}/revoke`, data ?? {});
  },

  // ── Taxonomy (Cause / Barrier Codes) ──────────────────────────────────────

  listCauseCodes(query?: { page?: number; limit?: number; category?: string; is_active?: boolean }) {
    return api.get<ApiResponse<HseCauseCode[]> & { meta: PaginationMeta }>('/hse/incidents/taxonomy/cause-codes', { params: query });
  },

  listBarrierCodes(query?: { page?: number; limit?: number; barrier_type?: string; is_active?: boolean }) {
    return api.get<ApiResponse<HseBarrierCode[]> & { meta: PaginationMeta }>('/hse/incidents/taxonomy/barrier-codes', { params: query });
  },

  linkFindingCauseCode(findingId: string, data: { cause_code_id: string; notes?: string }) {
    return api.post<ApiResponse<Record<string, unknown>>>(`/hse/incidents/taxonomy/findings/${findingId}/cause-codes`, data);
  },

  // FINDINGS-DRAWER-READ-HARDEN-1: list reads use the incident-scoped
  // backend path `/hse/incidents/:id/findings/:findingId/cause-codes`
  // (see investigation-enhancement.router.ts). The previous shape
  // `/hse/incidents/taxonomy/findings/...` matched the same Express
  // route with `id="taxonomy"`, which Zod rejected as non-UUID → 400.
  listFindingCauseCodes(incidentId: string, findingId: string) {
    return api.get<ApiResponse<Record<string, unknown>[]>>(`/hse/incidents/${incidentId}/findings/${findingId}/cause-codes`);
  },

  unlinkFindingCauseCode(findingId: string, linkId: string) {
    return api.delete(`/hse/incidents/taxonomy/findings/${findingId}/cause-codes/${linkId}`);
  },

  linkFindingBarrierCode(findingId: string, data: { barrier_code_id: string; barrier_status: string; notes?: string }) {
    return api.post<ApiResponse<Record<string, unknown>>>(`/hse/incidents/taxonomy/findings/${findingId}/barrier-codes`, data);
  },

  // FINDINGS-DRAWER-READ-HARDEN-1: same incident-scoping fix as cause-codes.
  listFindingBarrierCodes(incidentId: string, findingId: string) {
    return api.get<ApiResponse<Record<string, unknown>[]>>(`/hse/incidents/${incidentId}/findings/${findingId}/barrier-codes`);
  },

  unlinkFindingBarrierCode(findingId: string, linkId: string) {
    return api.delete(`/hse/incidents/taxonomy/findings/${findingId}/barrier-codes/${linkId}`);
  },

  // ── Quality Gates ─────────────────────────────────────────────────────────

  listQualityGates(query?: { page?: number; limit?: number; applies_to_transition?: string; is_active?: boolean }) {
    return api.get<ApiResponse<HseQualityGate[]> & { meta: PaginationMeta }>('/hse/incidents/quality-gates', { params: query });
  },

  listQualityGateEvaluations(incidentId: string) {
    return api.get<ApiResponse<HseQualityGateEvaluation[]>>(`/hse/incidents/${incidentId}/quality-gates/evaluations`);
  },

  evaluateQualityGates(incidentId: string, data: { transition: string }) {
    return api.post<ApiResponse<HseQualityGateEvaluation[]>>(`/hse/incidents/${incidentId}/quality-gates/evaluate`, data);
  },

  waiveQualityGate(incidentId: string, evaluationId: string, data: { waiver_reason: string }) {
    return api.post<ApiResponse<HseQualityGateEvaluation>>(`/hse/incidents/${incidentId}/quality-gates/evaluations/${evaluationId}/waive`, data);
  },

  // ── Five Whys ─────────────────────────────────────────────────────────────

  createFiveWhysChain(incidentId: string, data: Record<string, unknown>) {
    return api.post<ApiResponse<HseFiveWhysChain>>(`/hse/incidents/${incidentId}/five-whys`, data);
  },

  listFiveWhysChains(incidentId: string) {
    return api.get<ApiResponse<HseFiveWhysChain[]>>(`/hse/incidents/${incidentId}/five-whys`);
  },

  getFiveWhysChain(incidentId: string, chainId: string) {
    return api.get<ApiResponse<HseFiveWhysChain>>(`/hse/incidents/${incidentId}/five-whys/${chainId}`);
  },

  updateFiveWhysChain(incidentId: string, chainId: string, data: Record<string, unknown>) {
    return api.patch<ApiResponse<HseFiveWhysChain>>(`/hse/incidents/${incidentId}/five-whys/${chainId}`, data);
  },

  deleteFiveWhysChain(incidentId: string, chainId: string) {
    return api.delete(`/hse/incidents/${incidentId}/five-whys/${chainId}`);
  },

  addFiveWhysStep(incidentId: string, chainId: string, data: Record<string, unknown>) {
    return api.post<ApiResponse<HseFiveWhysStep>>(`/hse/incidents/${incidentId}/five-whys/${chainId}/steps`, data);
  },

  listFiveWhysSteps(incidentId: string, chainId: string) {
    return api.get<ApiResponse<HseFiveWhysStep[]>>(`/hse/incidents/${incidentId}/five-whys/${chainId}/steps`);
  },

  updateFiveWhysStep(incidentId: string, chainId: string, stepId: string, data: Record<string, unknown>) {
    return api.patch<ApiResponse<HseFiveWhysStep>>(`/hse/incidents/${incidentId}/five-whys/${chainId}/steps/${stepId}`, data);
  },

  deleteFiveWhysStep(incidentId: string, chainId: string, stepId: string) {
    return api.delete(`/hse/incidents/${incidentId}/five-whys/${chainId}/steps/${stepId}`);
  },

  // ── Fishbone Diagrams ─────────────────────────────────────────────────────

  createFishbone(incidentId: string, data: Record<string, unknown>) {
    return api.post<ApiResponse<HseFishboneDiagram>>(`/hse/incidents/${incidentId}/fishbone`, data);
  },

  listFishbones(incidentId: string) {
    return api.get<ApiResponse<HseFishboneDiagram[]>>(`/hse/incidents/${incidentId}/fishbone`);
  },

  getFishbone(incidentId: string, diagramId: string) {
    return api.get<ApiResponse<HseFishboneDiagram>>(`/hse/incidents/${incidentId}/fishbone/${diagramId}`);
  },

  updateFishbone(incidentId: string, diagramId: string, data: Record<string, unknown>) {
    return api.patch<ApiResponse<HseFishboneDiagram>>(`/hse/incidents/${incidentId}/fishbone/${diagramId}`, data);
  },

  deleteFishbone(incidentId: string, diagramId: string) {
    return api.delete(`/hse/incidents/${incidentId}/fishbone/${diagramId}`);
  },

  addFishboneBranch(incidentId: string, diagramId: string, data: Record<string, unknown>) {
    return api.post<ApiResponse<HseFishboneBranch>>(`/hse/incidents/${incidentId}/fishbone/${diagramId}/branches`, data);
  },

  listFishboneBranches(incidentId: string, diagramId: string) {
    return api.get<ApiResponse<HseFishboneBranch[]>>(`/hse/incidents/${incidentId}/fishbone/${diagramId}/branches`);
  },

  updateFishboneBranch(incidentId: string, diagramId: string, branchId: string, data: Record<string, unknown>) {
    return api.patch<ApiResponse<HseFishboneBranch>>(`/hse/incidents/${incidentId}/fishbone/${diagramId}/branches/${branchId}`, data);
  },

  deleteFishboneBranch(incidentId: string, diagramId: string, branchId: string) {
    return api.delete(`/hse/incidents/${incidentId}/fishbone/${diagramId}/branches/${branchId}`);
  },

  // ── Timeline Events ───────────────────────────────────────────────────────

  createTimelineEvent(incidentId: string, data: Record<string, unknown>) {
    return api.post<ApiResponse<HseTimelineEvent>>(`/hse/incidents/${incidentId}/timeline`, data);
  },

  listTimelineEvents(incidentId: string) {
    return api.get<ApiResponse<HseTimelineEvent[]>>(`/hse/incidents/${incidentId}/timeline`);
  },

  updateTimelineEvent(incidentId: string, eventId: string, data: Record<string, unknown>) {
    return api.patch<ApiResponse<HseTimelineEvent>>(`/hse/incidents/${incidentId}/timeline/${eventId}`, data);
  },

  deleteTimelineEvent(incidentId: string, eventId: string) {
    return api.delete(`/hse/incidents/${incidentId}/timeline/${eventId}`);
  },

  // ── Barrier Analysis ──────────────────────────────────────────────────────

  createBarrierAnalysis(incidentId: string, data: Record<string, unknown>) {
    return api.post<ApiResponse<HseBarrierAnalysis>>(`/hse/incidents/${incidentId}/barrier-analysis`, data);
  },

  listBarrierAnalyses(incidentId: string) {
    return api.get<ApiResponse<HseBarrierAnalysis[]>>(`/hse/incidents/${incidentId}/barrier-analysis`);
  },

  updateBarrierAnalysis(incidentId: string, analysisId: string, data: Record<string, unknown>) {
    return api.patch<ApiResponse<HseBarrierAnalysis>>(`/hse/incidents/${incidentId}/barrier-analysis/${analysisId}`, data);
  },

  deleteBarrierAnalysis(incidentId: string, analysisId: string) {
    return api.delete(`/hse/incidents/${incidentId}/barrier-analysis/${analysisId}`);
  },

  // ── Lessons Learned ───────────────────────────────────────────────────────

  createLesson(incidentId: string, data: Record<string, unknown>) {
    return api.post<ApiResponse<HseLessonLearned>>(`/hse/incidents/${incidentId}/lessons`, data);
  },

  listLessons(incidentId: string) {
    return api.get<ApiResponse<HseLessonLearned[]>>(`/hse/incidents/${incidentId}/lessons`);
  },

  // PR A — Incident Lifecycle Readiness (read-only advisory).
  // Calls GET /api/hse/incidents/:id/lifecycle-readiness. Returns the
  // G1–G7 gate readiness signal computed from current incident data
  // without changing status semantics or writing anything. Permission
  // gated on hse:incident:view (no new permission introduced).
  getIncidentLifecycleReadiness(incidentId: string) {
    return api.get<ApiResponse<HseIncidentLifecycleReadiness>>(
      `/hse/incidents/${incidentId}/lifecycle-readiness`,
    );
  },

  // PR OPM-B — Incident Operational Memory (read-only Operator Evidence
  // Ledger consumer). Calls GET /api/hse/incidents/:id/operational-memory,
  // sealed in PR OPM-A1 (PR #571). Returns a chronologically-sorted,
  // immutable read-model of evidence-grade memory items aggregated from
  // 8 existing source tables. Permission-gated server-side on
  // hse:incident:view (no new permission introduced). No mutation
  // sibling endpoint.
  getIncidentOperationalMemory(incidentId: string) {
    return api.get<ApiResponse<HseIncidentOperationalMemory>>(
      `/hse/incidents/${incidentId}/operational-memory`,
    );
  },

  // PR INV-TEAM-A1 — Investigation Team Mobilization (read-only).
  // Calls GET /api/hse/incidents/:id/investigation-team. Returns the
  // active members of the investigation team for one incident: internal
  // users (joined to public.users for display_name + email) and external
  // SMEs (via external_member_name). Removed members are excluded
  // server-side; `removed_count` is returned alongside so the UI can
  // optionally surface a removed-history badge without exposing per-row
  // PII for the removed cohort. Permission-gated server-side on
  // hse:investigation:team:view (already seeded by mig 085). No
  // mutation sibling endpoint in this lane.
  getInvestigationTeam(incidentId: string) {
    return api.get<ApiResponse<HseInvestigationTeamList>>(
      `/hse/incidents/${incidentId}/investigation-team`,
    );
  },

  // PR INV-STATEMENT-CONSENT-A2-2 — Statements & Consent (read-only).
  // Calls GET /api/hse/incidents/:id/statement-consent. Returns a compact,
  // data-minimized overview of the three statement / consent / participant
  // tables introduced by mig 381 (active rows + removed counts).
  // Server projects ONLY the non-sensitive fields under the
  // hse:incident:statement_consent:view permission (mig 382, sensitive).
  // Free-text / PII columns (contact_summary, external_reference,
  // guardian_name, notes_summary, statement_text) are NOT included in
  // the v1 payload. No mutation sibling endpoint in this lane.
  getStatementConsent(incidentId: string) {
    return api.get<ApiResponse<HseStatementConsentOverview>>(
      `/hse/incidents/${incidentId}/statement-consent`,
    );
  },

  // PR #553 — tenant-wide Lessons Learned Register read.
  // Calls GET /api/hse/incidents/lessons (literal-segment route, distinct
  // from the 2-segment incident-scoped GET /:id/lessons). Returns rows
  // augmented with `incident_number` + `incident_title` produced by a
  // tenant-safe LEFT JOIN inside `LessonLearnedService.listForRegister`.
  listLessonsRegister(query?: {
    page?: number;
    limit?: number;
    incident_id?: string;
    status?: string;
    category?: string;
  }) {
    return api.get<
      ApiResponse<HseLessonLearnedRegisterRow[]> & { meta: PaginationMeta }
    >('/hse/incidents/lessons', { params: query });
  },

  updateLesson(incidentId: string, lessonId: string, data: Record<string, unknown>) {
    return api.patch<ApiResponse<HseLessonLearned>>(`/hse/incidents/${incidentId}/lessons/${lessonId}`, data);
  },

  deleteLesson(incidentId: string, lessonId: string) {
    return api.delete(`/hse/incidents/${incidentId}/lessons/${lessonId}`);
  },

  // ── Safety Bulletins ──────────────────────────────────────────────────────

  createBulletin(data: Record<string, unknown>) {
    return api.post<ApiResponse<HseSafetyBulletin>>('/hse/incidents/bulletins', data);
  },

  listBulletins(query?: { page?: number; limit?: number; status?: string; severity?: string }) {
    return api.get<ApiResponse<HseSafetyBulletin[]> & { meta: PaginationMeta }>('/hse/incidents/bulletins', { params: query });
  },

  getBulletin(bulletinId: string) {
    return api.get<ApiResponse<HseSafetyBulletin>>(`/hse/incidents/bulletins/${bulletinId}`);
  },

  updateBulletin(bulletinId: string, data: Record<string, unknown>) {
    return api.patch<ApiResponse<HseSafetyBulletin>>(`/hse/incidents/bulletins/${bulletinId}`, data);
  },

  getBulletinSources(bulletinId: string) {
    return api.get<ApiResponse<BulletinSourceDisplay[]>>(
      `/hse/incidents/bulletins/${bulletinId}/sources`,
    );
  },

  // ── Training Recommendations ──────────────────────────────────────────────

  createTrainingRecommendation(incidentId: string, data: Record<string, unknown>) {
    return api.post<ApiResponse<HseTrainingRecommendation>>(`/hse/incidents/${incidentId}/training`, data);
  },

  listTrainingRecommendations(incidentId: string) {
    return api.get<ApiResponse<HseTrainingRecommendation[]>>(`/hse/incidents/${incidentId}/training`);
  },

  updateTrainingRecommendation(incidentId: string, recId: string, data: Record<string, unknown>) {
    return api.patch<ApiResponse<HseTrainingRecommendation>>(`/hse/incidents/${incidentId}/training/${recId}`, data);
  },

  deleteTrainingRecommendation(incidentId: string, recId: string) {
    return api.delete(`/hse/incidents/${incidentId}/training/${recId}`);
  },

  // ── Finding ↔ Evidence Links ──────────────────────────────────────────────

  linkFindingEvidence(incidentId: string, findingId: string, data: { evidence_id: string; relevance_note?: string }) {
    return api.post<ApiResponse<Record<string, unknown>>>(`/hse/incidents/${incidentId}/findings/${findingId}/evidence-links`, data);
  },

  listFindingEvidenceLinks(incidentId: string, findingId: string) {
    return api.get<ApiResponse<Record<string, unknown>[]>>(`/hse/incidents/${incidentId}/findings/${findingId}/evidence-links`);
  },

  unlinkFindingEvidence(incidentId: string, findingId: string, linkId: string) {
    return api.delete(`/hse/incidents/${incidentId}/findings/${findingId}/evidence-links/${linkId}`);
  },

  // ── Management Reviews (mig 089) ──────────────────────────────────────────
  // PR #546 shipped READ paths only.
  // PR #548 (this) adds POST item and PATCH item mutations consumed by the
  // create/edit modal at /hse/management-reviews/:reviewId.
  //
  // Backend permission map (apps/api/.../investigation-enhancement.router.ts):
  //   GET   list/sources/items → hse:management_review:view
  //   POST  /items             → hse:management_review:manage
  //   PATCH /items/:itemId     → hse:management_review:manage
  // No DELETE surface — soft disposition is PATCH status='cancelled'.

  listManagementReviews(query?: {
    page?: number;
    limit?: number;
    status?: string;
    chair_user_id?: string;
    facilitator_user_id?: string;
  }) {
    return api.get<ApiResponse<HseManagementReview[]> & { meta: PaginationMeta }>(
      '/hse/incidents/management-reviews',
      { params: query },
    );
  },

  getManagementReview(reviewId: string) {
    return api.get<ApiResponse<HseManagementReview>>(
      `/hse/incidents/management-reviews/${reviewId}`,
    );
  },

  getManagementReviewSources(reviewId: string) {
    return api.get<ApiResponse<ManagementReviewSourceDisplay[]>>(
      `/hse/incidents/management-reviews/${reviewId}/sources`,
    );
  },

  getManagementReviewItems(
    reviewId: string,
    query?: {
      page?: number;
      limit?: number;
      status?: string;
      item_type?: string;
    },
  ) {
    return api.get<
      ApiResponse<HseManagementReviewItem[]> & { meta: PaginationMeta }
    >(`/hse/incidents/management-reviews/${reviewId}/items`, { params: query });
  },

  // PR #548 — POST item. Body shape mirrors `createManagementReviewItemSchema`
  // in apps/api/src/modules/incident/investigation-schemas.ts. The frontend
  // never builds the body in place; serializeCreateManagementReviewItemPayload
  // owns the wire shape so all create rules (entity-pair lock-step,
  // completed_at-status invariant, blank omission) are testable in isolation.
  createManagementReviewItem(
    reviewId: string,
    body: CreateManagementReviewItemPayload,
  ) {
    return api.post<ApiResponse<HseManagementReviewItem>>(
      `/hse/incidents/management-reviews/${reviewId}/items`,
      body,
    );
  },

  // PR #548 — PATCH item. Diff-only body produced by
  // serializePatchManagementReviewItemPayload. `null` is meaningful here
  // for clearing optional fields; backend service `applyCompletedAtRule`
  // governs the completed_at⇔status invariant when status changes.
  updateManagementReviewItem(
    reviewId: string,
    itemId: string,
    body: UpdateManagementReviewItemPayload,
  ) {
    return api.patch<ApiResponse<HseManagementReviewItem>>(
      `/hse/incidents/management-reviews/${reviewId}/items/${itemId}`,
      body,
    );
  },
};
